import {ProgramPaths} from "@/enum/ProgramPaths";
import {TakipDurumu} from "@/enum/TakipDurumu";

/**
 * DEV-2857: Takip karti ileri/geri butonlarinin tek kaynagi.
 *
 * Butonlar eskiden `GET /takip/{id}/next|back` ile veritabanindaki ardisik id'ler
 * arasinda geziyordu; takip listesinde uygulanan filtre/arama/siralama karta
 * gecince kayboluyordu. Masaustu IcraPro'da ise karttan ileri/geri, arama sonuc
 * listesinin sirasini izler (78 -> 279 -> 442).
 *
 * Liste karttan acilirken o listenin ANLIK GORUNTUSU alinir ve gezinme bu sabit
 * dizide yapilir:
 * - once ekrandaki sayfanin id'leri yazilir (aninda gezilebilir), ayni anda
 *   listenin TAM sirasi `POST /takipler/sira` ile (listeyle ayni filtre + arama
 *   + siralama, yalniz id) istenir ve gelince sayfa listesinin yerine gecer;
 * - anlik goruntu sabittir: gezinirken kapatilan/acilan (filtreden dusen) takip
 *   yerinde kalir, komsulari degismez; sonradan eklenen takip gorunmez — masaustu
 *   arama sonuc listesiyle ayni anlam. Sayfa sayfa birlestirme olmadigi icin kayma,
 *   atlama, tekrar ya da erken "son takip" olusmaz;
 * - hedefin guncel statusu her adimda sorulur ki acilmamis takip `takip-ekle`
 *   ekranina gitsin; silinmis takip listeden cikarilip bir sonrakine gecilir.
 *
 * Tam sira alinamazsa (uc henuz dagitilmadi, hata) yalniz ekrandaki sayfa
 * icinde gezilir. Listeden gelinmeyen kartlarda (bildirim, ajanda, dogrudan
 * adres...) eski davranis (veritabani sirasi) korunur.
 */

export const TAKIP_LISTE_GEZINTI_ANAHTARI = 'takipListeGezinti';
export const TAKIP_LISTESI_YOLU: string = ProgramPaths.icrapro + '/takipler';
export const TAKIP_LISTE_SIRASI_ADRESI = '/api/v1/takipler/sira';

/** Ardisik silinmis takip atlama siniri (sonsuz donguye karsi). */
const SILINMIS_ATLAMA_SINIRI = 5;

const KART_ROTALARI: string[] = [
    ProgramPaths.icrapro + '/takip/:id',
    ProgramPaths.icrapro + '/takip-ekle/:id',
];

const SINIR_MESAJI = {
    ileri: 'Listedeki son takiptesiniz.',
    geri: 'Listedeki ilk takiptesiniz.',
};

const VERITABANI_UC_MESAJI = {
    ileri: 'Sonraki takip bulunamadı.',
    geri: 'Önceki takip bulunamadı.',
};

export type GezintiYonu = 'ileri' | 'geri';

/** Takip listesinin son gercek istegi (TakipList.load / loadInfiniteChunk). */
export interface TakipListeIstegi {
    /** Anahtar kelime + kart no aramasi; doluysa liste `/takipler/search` ile gelir. */
    query: string;
    govde: { sortBy: any[]; sortDesc: any[]; filters: { [key: string]: any } };
}

/** `bekliyor`: yalniz sayfa biliniyor; `alindi`: tam sira; `alinamadi`: sayfa ile yetinilir. */
export type TamSiraDurumu = 'bekliyor' | 'alindi' | 'alinamadi';

export interface TakipListeBaglami extends TakipListeIstegi {
    surum: 1;
    tenantId: string | null;
    /** Her liste girisinde yenilenir; gec gelen tam sira yanitinin baska baglama yazilmasini onler. */
    kimlik: string;
    /** Takip id'leri, liste sirasiyla, tekil. */
    liste: number[];
    tamSira: TamSiraDurumu;
    /** En son acilan takip karti. */
    aktifId: number;
}

export type KomsuSonucu =
    | { tur: 'hedef'; id: number }
    | { tur: 'sinir' }
    | { tur: 'baglamYok' };

export type ListePostFn = (url: string, govde: any) => Promise<any>;

export interface GezintiAraclari {
    http: { get: (url: string) => Promise<any>; post: ListePostFn };
    router: { push: (yol: string) => any };
    toast: { info: (mesaj: string) => any };
}

/** Router'in `to`/`from` nesnesinden ihtiyac duyulan kadari. */
export interface GezintiRotasi {
    path: string;
    params?: { [key: string]: any };
    matched?: Array<{ path: string }>;
}

function etkinTenant(): string | null {
    return sessionStorage.getItem('tenantId');
}

function yeniKimlik(): string {
    return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

/** Sayilastirir, gecersizleri atar, ilk gorulen sirayi koruyarak tekillestirir. */
function tekilIdler(degerler: any[]): number[] {
    const idler = degerler.map(deger => Number(deger)).filter(id => Number.isFinite(id));
    return Array.from(new Set(idler));
}

function baglamYaz(baglam: TakipListeBaglami): void {
    try {
        sessionStorage.setItem(TAKIP_LISTE_GEZINTI_ANAHTARI, JSON.stringify(baglam));
    } catch (hata) {
        // Kota/gizli mod: gezinme eski davranisa (veritabani sirasi) duser.
        console.warn('Takip listesi gezinti bağlamı kaydedilemedi:', hata);
    }
}

export function takipListeBaglaminiTemizle(): void {
    try {
        sessionStorage.removeItem(TAKIP_LISTE_GEZINTI_ANAHTARI);
    } catch (hata) {
        // erisilemeyen depolama: silinecek bir sey de yoktur
    }
}

export function takipListeBaglaminiOku(): TakipListeBaglami | null {
    let baglam: any;
    try {
        const ham = sessionStorage.getItem(TAKIP_LISTE_GEZINTI_ANAHTARI);
        if (!ham) return null;
        baglam = JSON.parse(ham);
    } catch (hata) {
        return null;
    }
    if (!baglam || baglam.surum !== 1 || !Array.isArray(baglam.liste)) return null;
    if (baglam.tenantId !== etkinTenant()) {
        // Ayni sekmede baska buroya gecildi: o buronun listesi burada gecersiz.
        takipListeBaglaminiTemizle();
        return null;
    }
    return baglam as TakipListeBaglami;
}

let bekleyenTamSira: { kimlik: string; soz: Promise<boolean> } | null = null;

/**
 * Baglamdaki listenin tam sirasini ister ve gelince sayfa listesinin yerine
 * yazar. Ayni baglam icin tek istek atilir; basarisizsa bir daha denenmez
 * (her tiklamada hata bildirimi cikmasin).
 */
export function takipListeTamSirasiniYukle(post: ListePostFn): Promise<boolean> {
    const baglam = takipListeBaglaminiOku();
    if (!baglam) return Promise.resolve(false);
    if (baglam.tamSira !== 'bekliyor') return Promise.resolve(baglam.tamSira === 'alindi');
    if (bekleyenTamSira && bekleyenTamSira.kimlik === baglam.kimlik) return bekleyenTamSira.soz;

    const kimlik = baglam.kimlik;
    const sonucuYaz = (idler: number[] | null): boolean => {
        const guncel = takipListeBaglaminiOku();
        if (!guncel || guncel.kimlik !== kimlik) return false; // bu arada baska listeden kart acildi
        // Tam sira, acilan takibi icermiyorsa (liste o arada degisti) sayfa listesiyle yetinilir.
        if (idler && idler.includes(guncel.aktifId)) {
            guncel.liste = idler;
            guncel.tamSira = 'alindi';
        } else {
            guncel.tamSira = 'alinamadi';
        }
        baglamYaz(guncel);
        return guncel.tamSira === 'alindi';
    };

    const soz = post(TAKIP_LISTE_SIRASI_ADRESI, {query: baglam.query, ...baglam.govde})
        .then((yanit: any) => sonucuYaz(yanit && Array.isArray(yanit.data) ? tekilIdler(yanit.data) : null))
        .catch((hata: any) => {
            // HTTP hatasini `$http` yanit kesicisi zaten gosterir; sayfa icinde gezilir.
            console.warn('Takip listesinin tam sırası alınamadı:', hata);
            return sonucuYaz(null);
        })
        .finally(() => {
            if (bekleyenTamSira && bekleyenTamSira.kimlik === kimlik) bekleyenTamSira = null;
        });
    bekleyenTamSira = {kimlik, soz};
    return soz;
}

/**
 * Liste karttan acilirken cagrilir: ekrandaki satirlarin (liste sirasiyla) id'leri
 * yazilir ve `post` verilirse listenin tam sirasi arka planda istenir.
 */
export function takipListeBaglaminiKaydet(
    istek: TakipListeIstegi,
    kayitlar: Array<{ id: any }>,
    aktifId: number,
    post?: ListePostFn,
): void {
    baglamYaz({
        surum: 1,
        tenantId: etkinTenant(),
        kimlik: yeniKimlik(),
        query: istek.query || '',
        // Kopya: liste formundaki sonraki (aranmamis) degisiklikler baglama sizmasin.
        govde: JSON.parse(JSON.stringify(istek.govde)),
        liste: tekilIdler(kayitlar.map(kayit => kayit && kayit.id)),
        tamSira: 'bekliyor',
        aktifId: Number(aktifId),
    });
    if (post) {
        takipListeTamSirasiniYukle(post);
    }
}

/**
 * Takip listesi yanitini ({total, data}, ham ES `hits.hits` ya da diziyi
 * barindiran nesne) satirlara ve toplam kayit sayisina cozer.
 * `$http` yanit kesicisi govdeyi dondurdugu icin `yanit.data` satirlardir.
 */
export function takipListeYanitiniCoz(yanit: any): { kayitlar: any[]; toplam: number } {
    if (!yanit || !yanit.data) return {kayitlar: [], toplam: 0};

    if (Array.isArray(yanit.data)) {
        const kayitlar = yanit.data.map((item: any) => item._source || item);
        const toplam = Object.prototype.hasOwnProperty.call(yanit, 'total') && typeof yanit.total === 'number'
            ? yanit.total : kayitlar.length;
        return {kayitlar, toplam};
    }

    if (typeof yanit.data === 'object' && yanit.data.hits && Array.isArray(yanit.data.hits.hits)) {
        const kayitlar = yanit.data.hits.hits.map((hit: any) => hit._source || hit);
        let toplam = kayitlar.length;
        if (yanit.data.hits.total && typeof yanit.data.hits.total === 'number') {
            toplam = yanit.data.hits.total;
        } else if (yanit.data.hits.total && typeof yanit.data.hits.total.value === 'number') {
            toplam = yanit.data.hits.total.value;
        }
        return {kayitlar, toplam};
    }

    if (typeof yanit.data === 'object') {
        const dizi = Object.values(yanit.data).find(deger => Array.isArray(deger)) as any[] | undefined;
        if (dizi && dizi.length) {
            const kayitlar = dizi.map((item: any) => item._source || item);
            const toplam = Object.prototype.hasOwnProperty.call(yanit.data, 'total') && typeof yanit.data.total === 'number'
                ? yanit.data.total : dizi.length;
            return {kayitlar, toplam};
        }
    }

    return {kayitlar: [], toplam: 0};
}

/** Acilmamis takip `takip-ekle` ekraninda, digerleri takip kartinda acilir. */
export function kartRotasi(id: number | string, takipDurumu: string | null | undefined): string {
    const ekran = takipDurumu === TakipDurumu.acilmamis ? 'takip-ekle' : 'takip';
    return `${ProgramPaths.icrapro}/${ekran}/${id}`;
}

/** Rota bir takip karti ise takip id'si, degilse null (`takip-ekle/yeni` kart degildir). */
export function kartRotasiTakipId(rota: GezintiRotasi | null | undefined): number | null {
    if (!rota || !rota.matched || !rota.matched.some(kayit => KART_ROTALARI.includes(kayit.path))) {
        return null;
    }
    const id = Number(rota.params && rota.params.id);
    return Number.isInteger(id) && id > 0 ? id : null;
}

/**
 * `router.afterEach`:
 * - kart listeden ya da baska bir karttan (ileri/geri, tarayici geri/ileri)
 *   acildiysa ve takip bu listedeyse `aktifId` ona tasinir;
 * - kart baska bir yerden (bildirim, ajanda, is listesi, yeni sekme...) son
 *   gezilen takip disinda bir takip icin acildiysa eski liste baglami silinir.
 *   Ayni takibe donus (ajandadan geri, sayfa yenileme) baglami korur.
 */
export function takipListeBaglaminiRotayaGoreDogrula(to: GezintiRotasi, from: GezintiRotasi): void {
    const hedefId = kartRotasiTakipId(to);
    if (hedefId === null) return;

    const baglam = takipListeBaglaminiOku();
    if (!baglam) return;

    const listedenYaDaKarttan = kartRotasiTakipId(from) !== null || (!!from && from.path === TAKIP_LISTESI_YOLU);
    if (listedenYaDaKarttan) {
        if (baglam.aktifId !== hedefId && baglam.liste.includes(hedefId)) {
            baglam.aktifId = hedefId;
            baglamYaz(baglam);
        }
        return;
    }
    if (baglam.aktifId !== hedefId) {
        takipListeBaglaminiTemizle();
    }
}

/**
 * Liste baglaminda `mevcutId`nin bir sonraki/onceki takibini bulur. Tam sira
 * henuz gelmediyse once onu bekler (sayfa yenilendiyse o anda ister). Baglam
 * yoksa ya da bu takip o listede degilse `baglamYok` doner (cagiran eski
 * davranisa duser).
 */
export async function listedekiKomsuyuBul(mevcutId: number, yon: GezintiYonu, post: ListePostFn): Promise<KomsuSonucu> {
    let baglam = takipListeBaglaminiOku();
    if (!baglam) return {tur: 'baglamYok'};

    if (baglam.tamSira === 'bekliyor') {
        await takipListeTamSirasiniYukle(post);
        baglam = takipListeBaglaminiOku();
        if (!baglam) return {tur: 'baglamYok'};
    }

    const i = baglam.liste.indexOf(mevcutId);
    if (i < 0) {
        takipListeBaglaminiTemizle();
        return {tur: 'baglamYok'};
    }

    const hedef = baglam.liste[yon === 'ileri' ? i + 1 : i - 1];
    return hedef === undefined ? {tur: 'sinir'} : {tur: 'hedef', id: hedef};
}

function listedenCikar(id: number): void {
    const baglam = takipListeBaglaminiOku();
    if (!baglam) return;
    baglam.liste = baglam.liste.filter(kayitId => kayitId !== id);
    baglamYaz(baglam);
}

async function veritabaniSirasiylaGit(mevcutId: number, yon: GezintiYonu, araclar: GezintiAraclari): Promise<void> {
    const yanit = await araclar.http.get(`/api/v1/takip/${mevcutId}/${yon === 'ileri' ? 'next' : 'back'}`);
    const hedef = yanit && yanit.data;
    if (!hedef || hedef.id === null || hedef.id === undefined) {
        araclar.toast.info(VERITABANI_UC_MESAJI[yon]);
        return;
    }
    araclar.router.push(kartRotasi(hedef.id, hedef.takip_durumu));
}

let gezintiSuruyor = false;

/**
 * Takip kartindaki ileri (`ileri`) / geri (`geri`) butonlarinin isleyicisi.
 * `aktifId`, gecis tamamlaninca `router.afterEach` tarafindan tasinir.
 */
export async function takipKomsusunaGit(mevcutId: number, yon: GezintiYonu, araclar: GezintiAraclari): Promise<void> {
    if (gezintiSuruyor || !Number.isFinite(mevcutId)) return;
    gezintiSuruyor = true;
    const post: ListePostFn = (url, govde) => araclar.http.post(url, govde);
    try {
        for (let deneme = 0; deneme <= SILINMIS_ATLAMA_SINIRI; deneme++) {
            const sonuc = await listedekiKomsuyuBul(mevcutId, yon, post);
            if (sonuc.tur === 'baglamYok') {
                await veritabaniSirasiylaGit(mevcutId, yon, araclar);
                return;
            }
            if (sonuc.tur === 'sinir') {
                araclar.toast.info(SINIR_MESAJI[yon]);
                return;
            }
            const durumYaniti = await araclar.http.get(`/api/v1/takip/${sonuc.id}/takip-durumu`);
            const durum = durumYaniti && durumYaniti.data;
            if (durum) {
                araclar.router.push(kartRotasi(sonuc.id, durum));
                return;
            }
            // Liste acildiktan sonra silinmis: listeden cikar, bir sonrakini dene.
            listedenCikar(sonuc.id);
        }
        araclar.toast.info(SINIR_MESAJI[yon]);
    } catch (hata) {
        // HTTP hatasini `$http` yanit kesicisi (StatusCodeHelper) zaten gosterir.
        console.error('Takipler arasında geçiş yapılamadı:', hata);
    } finally {
        gezintiSuruyor = false;
    }
}
